import * as qs from 'query-string';
import find from 'lodash-es/find';
import { createSelector } from 'reselect';
import { nn, PathUtils, se } from 'module/Common';
import { PreviewType, Route, RouteType } from 'module/Model/Routes';
import { State } from '../index';

export namespace RouterSelector {
  export const selectLocation = (s: State) => se(s, 'location', s.location);
  export const selectPathname = (s: State) => se(s, 'location', s.location).pathname;
  export const selectRequested = (s: State) => se(s, 'requested', s.requested);

  const selectRoutes = (s: State) => se(s, 'routes', s.routes);

  export const selectLastGridRoute = (s: State) => se(s, 'lastGridRoute', s.lastGridRoute);

  export const selectSearch = (s: State) => se(s, 'location', s.location).search;

  export const selectHomeRoute = (s: State) => nn(routeByTypeId(s, RouteType.Home));

  export const selectLastGridRouteOrHome = (s: State): Route => selectLastGridRoute(s) || selectHomeRoute(s);

  export const selectSearchObject = createSelector(selectSearch, searchStr => qs.parse(searchStr));

  export const selectLang = createSelector(selectPathname, (pathname): PathUtils.Lang => {
    return PathUtils.getLangOnPath(pathname);
  });

  export const selectOtherLang = createSelector(selectLang, (lang): PathUtils.Lang => {
    return lang === 'fr' ? 'en' : 'fr';
  });

  export const selectPreview = (s: State) => se(s, 'preview', s.preview);

  export const isPreview = createSelector(selectPreview, selectSearchObject, (preview, searchObj) => {
    if (preview === null) {
      return false;
    }
    if (preview.type === PreviewType.Page) {
      return searchObj && searchObj.preview && searchObj.page_id && parseInt(searchObj.page_id, 10) === preview.id;
    }
    return searchObj && searchObj.preview && searchObj.p && parseInt(searchObj.p, 10) === preview.id;
  });

  export const selectPathnameHasLang = createSelector(selectPathname, (pathname): boolean => {
    return PathUtils.pathHasLang(pathname);
  });

  export const selectRoute = createSelector(selectPathname, (pathname): string => {
    return PathUtils.removeLangOnPath(pathname);
  });

  export const selectRequestedRoute = createSelector(selectRequested, (requested): string | null => {
    return requested.location === null ? null : PathUtils.removeLangOnPath(requested.location.pathname);
  });

  export const selectMatchingRoute = createSelector(
    selectRoute,
    selectRoutes,
    selectPathname,
    (route, routes, pathname): Route => {
      const routeObj = routes[route];
      if (routeObj) {
        return routeObj;
      }
      const path = PathUtils.removeLangOnPath(pathname);
      return {
        type: RouteType.NotFound,
        id: null,
        path,
        link: '',
      };
    }
  );

  export const selectRequestedMatchingRoute = createSelector(
    selectRequestedRoute,
    selectRoutes,
    selectPathname,
    (route, routes, pathname): Route | null => {
      if (route === null) {
        return null;
      }
      const routeObj = routes[route];
      if (routeObj) {
        return routeObj;
      }
      const path = PathUtils.removeLangOnPath(pathname);
      return {
        type: RouteType.NotFound,
        id: null,
        path,
        link: '',
      };
    }
  );

  export const selectMatchingRouteType = createSelector(selectMatchingRoute, (route): RouteType | null => {
    if (!route) {
      return null;
    }
    return route.type;
  });

  export const routeIsMatching = (
    s: State,
    routeType: RouteType,
    routeId: number | null = null,
    lang?: PathUtils.Lang
  ): boolean => {
    const route = selectMatchingRoute(s);
    const langMatching = lang === undefined ? true : selectLang(s) === lang;
    if (!route) {
      return false;
    }
    return langMatching && route.type === routeType && route.id === routeId;
  };

  export const routeByTypeId = (s: State, routeType: RouteType, routeId: number | null = null): Route | null => {
    const routes = selectRoutes(s);
    const routeKey = find(Object.keys(routes), k => {
      return routes[k].type === routeType && routes[k].id === routeId;
    });
    if (routeKey) {
      return routes[routeKey];
    }
    return null;
  };

  export const routePathByTypeAndId = (
    s: State,
    routeType: RouteType,
    routeId: number | null = null,
    lang?: PathUtils.Lang
  ): string | null => {
    const theLang = lang === undefined ? selectLang(s) : lang;
    if (routeType === RouteType.NotFound) {
      return PathUtils.addLangToPath(selectMatchingRoute(s).path, theLang);
    }
    const route = routeByTypeId(s, routeType, routeId);
    if (!route) {
      console.warn('route not found ??');
      return null;
    }
    return PathUtils.addLangToPath(route.path, theLang);
  };

  export const routeIsInExitState = createSelector(
    selectMatchingRoute,
    selectRequestedMatchingRoute,
    (currentRoute, requestedRoute): boolean => {
      if (requestedRoute === null) {
        return false;
      }
      if (currentRoute.type !== requestedRoute.type) {
        return true;
      }
      if (currentRoute.id !== requestedRoute.id) {
        return true;
      }
      return false;
    }
  );

  export const itemGridIsInExitState = createSelector(
    selectMatchingRoute,
    selectRequestedMatchingRoute,
    (currentRoute, requestedRoute): boolean => {
      if (currentRoute.type !== RouteType.Home && currentRoute.type !== RouteType.Category) {
        return false;
      }
      if (requestedRoute && requestedRoute.type !== RouteType.Home && requestedRoute.type !== RouteType.Category) {
        return true;
      }
      return false;
    }
  );
}



// WEBPACK FOOTER //
// ./src/module/Redux/Router/RouterSelector.ts